import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  getDocs,
  query,
  where
} from 'firebase/firestore';
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Modal from 'react-modal';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import ShopLogo from "../images/shop_logo.png";
import { SHOP_NAME, SHOP_ADDRESS, SHOP_PHONE } from "../Variables.js";

Modal.setAppElement('#root');

// Date formatter for DD MMM YYYY (e.g., 07 Oct 2025)
const dateFormatter = new Intl.DateTimeFormat('en-NG', {
  day: '2-digit',
  month: 'short',
  year: 'numeric'
});

// Date-time formatter for header
const formatDateTime = (date) => {
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).replace(',', '');
};

const TrialBalance = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [accounts, setAccounts] = useState([]);
  const [journalEntries, setJournalEntries] = useState([]);
  const [trialBalanceData, setTrialBalanceData] = useState([]);
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);
  const [isFetching, setIsFetching] = useState(false);
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);

  const accountsCollectionRef = collection(db, "accounts");
  const journalEntriesCollectionRef = collection(db, "journalEntries");

  useEffect(() => {
    if (!user || !user.uid || !userData) {
      console.log("Waiting for user authentication or userData...");
      setLoading(true);
      return;
    }
    setLoading(false);
    fetchAccounts();
  }, [user, userData]);

  useEffect(() => {
    if (accounts.length > 0) {
      generateTrialBalance();
    }
  }, [asOfDate, accounts]);

  const fetchAccounts = async () => {
    if (!user?.uid) {
      console.error("No user ID available for fetching accounts");
      setError("User not authenticated");
      setShowModal(true);
      return;
    }
    try {
      const q = query(accountsCollectionRef);
      const data = await getDocs(q);
      const accountList = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setAccounts(accountList);
    } catch (error) {
      console.error("Failed to fetch accounts:", error.message, error.code);
      setError(`Failed to fetch accounts: ${error.message} (${error.code})`);
      setShowModal(true);
    }
  };

  const fetchJournalEntries = async () => {
    if (!user?.uid) {
      console.error("No user ID available for fetching journal entries");
      setError("User not authenticated");
      setShowModal(true);
      return;
    }
    try {
      const q = query(journalEntriesCollectionRef);
      const data = await getDocs(q);
      const entryList = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setJournalEntries(entryList);
      return entryList;
    } catch (error) {
      console.error("Failed to fetch journal entries:", error.message, error.code);
      setError(`Failed to fetch journal entries: ${error.message} (${error.code})`);
      setShowModal(true);
      return [];
    }
  };

  const calculateAccountBalance = (accountId, accountName, entries) => {
    let balance = 0;
    
    entries.forEach(entry => {
      // Only include entries up to the selected date
      if (new Date(entry.date) <= new Date(asOfDate)) {
        entry.lines.forEach(line => {
          if (line.accountName === accountName || (line.accountId && line.accountId === accountId)) {
            const debit = Number(line.debit) || 0;
            const credit = Number(line.credit) || 0;
            balance += debit - credit;
          }
        });
      }
    });
    
    return balance;
  };

  const generateTrialBalance = async () => {
    if (accounts.length === 0) return;
    
    setIsFetching(true);
    try {
      const entries = await fetchJournalEntries();
      
      const trialBalance = accounts.map(account => {
        const balance = calculateAccountBalance(account.id, account.name, entries);
        
        return {
          id: account.id,
          code: account.code || 'N/A',
          name: account.name,
          type: account.type || 'Unknown',
          debit: balance > 0 ? Math.abs(balance) : 0,
          credit: balance < 0 ? Math.abs(balance) : 0,
          balance: balance
        };
      });
     
      // Sort by account code (with proper numeric sorting: 1000 before 2000, etc.)
      trialBalance.sort((a, b) => {
        const codeA = String(a.code || '').trim();
        const codeB = String(b.code || '').trim();

        // If both have codes → natural numeric sort
        if (codeA && codeB) {
          return codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: 'base' });
        }
        // If only one has code → put the one with code first
        if (codeA) return -1;
        if (codeB) return 1;
        // Otherwise sort by name
        return String(a.name || '').localeCompare(String(b.name || ''));
      });

      setTrialBalanceData(trialBalance);
    } catch (error) {
      console.error("Error generating trial balance:", error);
      setError("Failed to generate trial balance");
      setShowModal(true);
    } finally {
      setIsFetching(false);
    }
  };

  const getTotals = () => {
    const totalDebit = trialBalanceData.reduce((sum, account) => sum + account.debit, 0);
    const totalCredit = trialBalanceData.reduce((sum, account) => sum + account.credit, 0);
    return { totalDebit, totalCredit };
  };

  const isBalanced = () => {
    const { totalDebit, totalCredit } = getTotals();
    return Math.abs(totalDebit - totalCredit) < 0.01; // Allow for floating point precision
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    const { totalDebit, totalCredit } = getTotals();
    const fullName = userData?.firstName && userData?.lastName
      ? `${userData.firstName} ${userData.lastName}`
      : user?.displayName || "Unknown";

    // Header
    let currentY = 10;
    try {
      doc.addImage(ShopLogo, "PNG", 14, currentY, 15, 15);
    } catch (err) {
      console.log("Logo not loaded for PDF");
    }

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(SHOP_NAME, 35, currentY + 5);
    
    doc.setFontSize(14);
    doc.setFont("helvetica", "normal");
    doc.text("Trial Balance", 35, currentY + 13);
    
    doc.setFontSize(10);    
    doc.text(`Generated by: ${fullName}`, 35, currentY + 18);
    doc.text(`Generated on: ${formatDateTime(new Date())}`, 35, currentY + 23);
    doc.text(`As of ${dateFormatter.format(new Date(asOfDate))}`, 35, currentY + 28);    

    currentY += 35;

    // Table data
    const tableData = trialBalanceData.map(account => [
      account.code,
      account.name,
      account.type,
      `N${account.debit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      `N${account.credit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    ]);

    // Add totals row
    tableData.push([
      '',
      '',
      'TOTAL',
      `N${totalDebit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      `N${totalCredit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    ]);

    // Balance status
    const balanceStatus = isBalanced() ? "BALANCED" : "OUT OF BALANCE";

    autoTable(doc, {
      startY: currentY,
      head: [['Acc Code', 'Acc Name', 'Type', 'Debit (N)', 'Credit (N)']],
      body: tableData,
      theme: 'grid',
      styles: { 
        fontSize: 8,
        cellPadding: 2,
        textColor: 0,
      },
      headStyles: { 
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 9
      },
      columnStyles: {
        0: { cellWidth: 25, halign: 'left' },
        1: { cellWidth: 70, halign: 'left' },
        2: { cellWidth: 40, halign: 'left' },
        3: { cellWidth: 30, halign: 'right' },
        4: { cellWidth: 30, halign: 'right' }
      },
      margin: { left: 8, right: 14 },
      didDrawPage: (data) => {
        // Footer with balance status
        if (data.pageNumber === data.pageCount) {
          const finalY = data.cursor.y + 10;
          doc.setFontSize(10);
          doc.setTextColor(isBalanced() ? [0, 128, 0] : [255, 0, 0]);
          doc.text(`Status: ${balanceStatus}`, 14, finalY);
          doc.setTextColor(0, 0, 0);
          
          // Page numbers
          doc.text(
            `Page ${data.pageNumber} of ${data.pageCount}`,
            doc.internal.pageSize.width - 30,
            doc.internal.pageSize.height - 10
          );
        }
      }
    });

    doc.save(`TrialBalance_${asOfDate}.pdf`);
  };

  const refreshTrialBalance = () => {
    generateTrialBalance();
    setSuccessMessage("Trial Balance refreshed successfully");
    setShowModal(true);
  };

  if (loading) return <Loader text={"Loading"} />;
  if (!userData) return <Loader text={"Reading"} />;

  const allowedRoles = ['Accountant', 'Admin', 'Supreme'];
  const { totalDebit, totalCredit } = getTotals();

  return (
    <>
      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <h2 className="manage-page-heading caps size4 margin-top-cashflow1">Trial Balance</h2>
            <Link to="#" className="home-link" onClick={refreshTrialBalance}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>

          <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed">
            {/* Controls */}
            <div className="row gap-15 align-center justify-center" style={{ marginTop: '10px' }}>
              <div className="row gap-1 align-center">
                {/* <label className="input-label bold">As of Date</label> */}
                <input
                  type="date"
                  className="input-field1"
                  value={asOfDate}
                  onChange={(e) => setAsOfDate(e.target.value)}
                  style={{ marginBottom: '0px' }}
                />
              </div>
              {/* <button 
                className="login-button login-button-d caps" 
                onClick={() => setIsDateModalOpen(true)}
                style={{ marginTop: '25px' }}
              >
                Change Date
              </button> */}
              <button 
                className="login-button login-button-d caps" 
                onClick={generatePDF}
                disabled={trialBalanceData.length === 0}
                style={{ marginTop: '0px' }}
              >
                DOWNLOAD
              </button>
            </div>

            {/* Balance Status */}
            <div className={`balance-status ${isBalanced() ? 'balanced' : 'unbalanced'}`}>
              <div className="row align-center gap-1">
                <i className={`fas ${isBalanced() ? 'fa-check-circle' : 'fa-exclamation-triangle'}`}></i>
                <span className="bold">
                  {isBalanced() ? 'BALANCED' : 'OUT OF BALANCE'}
                </span>
              </div>
              <div className="row gap-1 totals-row">
                <span>Total Debit: <strong>N{totalDebit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></span>
                <span>Total Credit: <strong>N{totalCredit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></span>
                <span>Difference: <strong>N{Math.abs(totalDebit - totalCredit).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></span>
              </div>
            </div>

            {/* Trial Balance Table */}
            <div className="scroll-fixed scroll-fixed1">
              <div className="class-list class-list-d scrollable">
                {isFetching ? (
                  <p className="loading-text">Generating Trial Balance...</p>
                ) : trialBalanceData.length === 0 ? (
                  <p className="loading-text">No accounts found or no transactions available.</p>
                ) : (
                  <div className="trial-balance-table">
                    <div className="table-header row">
                      <div className="col-account-code">Code</div>
                      <div className="col-account-name">Account Name</div>
                      <div className="col-account-type">Type</div>
                      <div className="col-amount">Debit (N)</div>
                      <div className="col-amount">Credit (N)</div>
                    </div>
                    {trialBalanceData.map((account) => (
                      <div key={account.id} className="table-row row">
                        <div className="col-account-code">{account.code}</div>
                        <div className="col-account-name">{account.name}</div>
                        <div className="col-account-type">{account.type}</div>
                        <div className="col-amount">
                          {account.debit > 0 ? `N${account.debit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                        </div>
                        <div className="col-amount">
                          {account.credit > 0 ? `N${account.credit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                        </div>
                      </div>
                    ))}
                    {/* Totals Row */}
                    <div className="table-footer row bold">
                      <div className="col-account-code"></div>
                      <div className="col-account-name"></div>
                      <div className="col-account-type">TOTAL</div>
                      <div className="col-amount">N{totalDebit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                      <div className="col-amount">N{totalCredit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Date Selection Modal */}
          <Modal
            isOpen={isDateModalOpen}
            onRequestClose={() => setIsDateModalOpen(false)}
            contentLabel="Select Date"
            className="custom-modal"
            overlayClassName="custom-modal-overlay"
          >
            <h2>Select Trial Balance Date</h2>
            <form className="session-form">
              <label className="input-label">As of Date</label>
              <input
                type="date"
                className="input-field"
                value={asOfDate}
                onChange={(e) => setAsOfDate(e.target.value)}
                required
              />
              <div className="row align-center gap-1">
                <button
                  className="login-button"
                  type="button"
                  onClick={() => setIsDateModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  className="login-button"
                  type="button"
                  onClick={() => {
                    setIsDateModalOpen(false);
                    generateTrialBalance();
                  }}
                >
                  Apply Date
                </button>
              </div>
            </form>
          </Modal>

          {showModal && (
            <SuccessModal
              icon={error ? "xmark" : "check"}
              iconColor={error ? "red" : "green"}
              message={error || successMessage}
              onClose={() => {
                setError("");
                setSuccessMessage("");
                setShowModal(false);
              }}
            />
          )}
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}

      <style jsx>{`
        .balance-status {
          padding: 15px;
          border-radius: 8px;
          margin: 10px 0;
          font-size: 16px;          
        }
        .balanced {
          background-color: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }
        .unbalanced {
          background-color: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }
        .totals-row {
          justify-content: center;
          margin-top: 10px;
          flex-wrap: wrap;
          gap: 20px;
        }
        .trial-balance-table {
          width: 100%;
        }
        .table-header, .table-row, .table-footer {
          display: grid;
          grid-template-columns: 80px 1fr 100px 120px 120px;
          gap: 10px;
          padding: 12px 15px;
          border-bottom: 1px solid #e0e0e0;
          align-items: center;
        }
        .table-header {
          background-color: #f8f9fa;
          font-weight: bold;
          border-bottom: 2px solid #dee2e6;
        }
        .table-footer {
          background-color: #e9ecef;
          border-top: 2px solid #dee2e6;
          border-bottom: none;
        }
        .col-account-code { font-family: monospace; }
        .col-amount { text-align: right; font-family: monospace; }
        
        @media (max-width: 768px) {
          .balance-status {
            padding: 5px 20px;
            border-radius: 8px;
            margin: 10px 0;
            width: 100%;
          }
          .table-header, .table-row, .table-footer {
            grid-template-columns: 60px 1fr 80px 100px 100px;
            font-size: 12px;
            padding: 8px 10px;
          }
          .totals-row {
            flex-direction: flex-start;
            justify-content: center;
            gap: 5px;
          }
        }
      `}</style>
    </>
  );
};

export default TrialBalance;